import React, { useEffect, useState } from 'react';
import { Auth, Logger } from 'aws-amplify';
import axios from 'axios';
import './MfaHandler.css';

const logger = new Logger('MfaHandler');

/**
 * Component to handle MFA verification
 */
const MfaHandler = () => {
  const [code, setCode] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    
    const checkAndSendMFAEmail = async () => {
      const session = await Auth.currentSession();
      // Use the access token: the MFA API authoritatively resolves the caller's
      // identity server-side via Cognito GetUser, which requires an access token.
      const accessToken = session.getAccessToken().getJwtToken();
      const email = sessionStorage.getItem('userEmail');

      const check = await axios.post(process.env.REACT_APP_MFA_CHECK_URL, { email }, {
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if(check.status === 200 && check.data.mfaVerified){
        sessionStorage.setItem('mfaCompleted', 'true');
        logger.debug('MFA completed, redirecting to app');
        // Redirect to the main app
        window.location.href = '/';
        return;
      }
      
      const res = await axios.post(process.env.REACT_APP_MFA_SEND_URL, { email }, {
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      logger.debug('send-mfa-code: response:', res.status)
      
      if (res.status === 200) {
        setCodeRequested(true);
      } else {
        setCodeRequested(false);
      }
    };

    checkAndSendMFAEmail();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {

      if(!codeRequested){
        setError('Failed to send MFA code.');
        return;
      }

      logger.debug('Verifying code:', code);
      const session = await Auth.currentSession();
      // Use the access token (see checkAndSendMFAEmail): required by the MFA
      // API's server-side Cognito GetUser identity resolution.
      const accessToken = session.getAccessToken().getJwtToken();
      const email = sessionStorage.getItem('userEmail');
      const res = await axios.post(process.env.REACT_APP_MFA_VERIFY_URL, { email, code }, {
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      logger.debug('verify-mfa-code: response:', res.status)
  
      if (res.status === 200) {
          sessionStorage.setItem('mfaCompleted', 'true');
          logger.debug('MFA completed, redirecting to app');
          // Redirect to the main app
          window.location.href = '/';
          return;
      } else {
          setError('Invalid verification code. Please try again.');
      }
      
    } catch (err) {
      logger.error('Error verifying code:', err);
      if (err.response && err.response.status === 401) {
        setError('Invalid verification code. Please try again.');
      } else {
        setError('Error verifying code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBackToSignIn = async () => {
    try {
      await Auth.signOut();
      window.location.href = '/login';
    } catch (error) {
      logger.error('Error signing out:', error);
      window.location.href = '/login';
    }
  };

  return (
    <div className="mfa-container">
      <h2 className="mfa-heading">Confirm Sign-In</h2>
      <p>Please enter the verification code sent to your email.</p>
      
      {error && (
        <div className="mfa-error">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <label htmlFor="code">Verification code</label>
        <input
          id="code"
          className="mfa-textfield"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter verification code"
          disabled={isLoading}
        />
        
        <div className="mfa-buttons-container">
          <button 
            type="button" 
            className="mfa-link"
            onClick={handleBackToSignIn}
          >
            Back to Sign In
          </button>
          
          <button
            type="submit"
            className="mfa-button"
            disabled={isLoading || !code}
          >
            {isLoading ? 'Verifying...' : 'Confirm'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MfaHandler;